import { selectFiles } from '@app/components/FileUpload/modules/fileUpload'
import { AssociatedContact } from '@app/models/AssociatedContact'
import { Attachment } from '@app/models/Attachment'
import { ListingProgressV2Status, ListingType } from '@app/models/enums'
import { FileUploadAttachment } from '@app/models/FileUploadAttachment'
import { IListing, Listing } from '@app/models/Listing'
import { ListingLeaseData } from '@app/models/ListingLeaseData'
import { ListingSaleData } from '@app/models/ListingSaleData'
import { OnlineListingService } from '@app/models/OnlineListingService'
import { Price } from '@app/models/Price'
import { Property } from '@app/models/Property'
import { RentalRate } from '@app/models/RentalRate'
import { ITenantRent } from '@app/models/TenantRent'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import {
  IRequestOptions,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { List, Map, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector, createStructuredSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IListingEditFiles {
  flyerFiles?: Map<string, FileUploadAttachment>
  marketingPackageFiles?: Map<string, FileUploadAttachment>
}

export interface IListingEditState {
  readonly error?: string
  readonly isLoading: boolean
  readonly isReady: boolean
  readonly isSaving: boolean
  readonly listing: IListing
}

const ListingEditStateRecord = Record({
  error: undefined,
  isLoading: false,
  isReady: false,
  isSaving: false,
  listing: new Listing(),
})

export class ListingEditState extends ListingEditStateRecord
  implements IListingEditState {
  public static fromJS(initialState?: Partial<IListingEditState>) {
    if (!initialState) {
      return new ListingEditState()
    }

    const { listing, ...rest } = initialState
    return new ListingEditState({
      listing: Listing.fromJS(listing),
      ...rest,
    })
  }

  public readonly error?: string
  public readonly isLoading: boolean
  public readonly isReady: boolean
  public readonly isSaving: boolean
  public readonly listing: Listing

  constructor(props?: Partial<ListingEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  AddLinkedImage = 'LISTING_EDIT::ADD_LINKED_IMAGE',
  AddOnlineListingService = 'LISTING_EDIT::ADD_ONLINE_LISTING_SERVICE',
  DeleteLinkedImage = 'LISTING_EDIT::DELETE_LINKED_IMAGE',
  DeleteOnlineListingService = 'LISTING_EDIT::DELETE_ONLINE_LISTING_SERVICE',
  LoadAddListing = 'LISTING_EDIT::LOAD_ADD_LISTING',
  LoadEditListing = 'LISTING_EDIT::LOAD_EDIT_LISTING',
  LoadListingFailure = 'LISTING_EDIT::LOAD_LISTING_FAILURE',
  LoadListingRequest = 'LISTING_EDIT::LOAD_LISTING_REQUEST',
  LoadListingSuccess = 'LISTING_EDIT::LOAD_LISTING_SUCCESS',
  ResetState = 'LISTING_EDIT::RESET_STATE',
  SaveListing = 'LISTING_EDIT::SAVE_LISTING',
  SaveListingFailure = 'LISTING_EDIT::SAVE_LISTING_FAILURE',
  SaveListingRequest = 'LISTING_EDIT::SAVE_LISTING_REQUEST',
  SaveListingSuccess = 'LISTING_EDIT::SAVE_LISTING_SUCCESS',
  SetActiveDate = 'LISTING_EDIT::SET_ACTIVE_DATE',
  SetAdjoiningPropertyOwners = 'LISTING_EDIT::SET_ADJOINING_PROPERTY_OWNERS',
  SetAskingPrice = 'LISTING_EDIT::SET_ASKING_PRICE',
  SetCapRate = 'LISTING_EDIT::SET_CAP_RATE',
  SetDateAvailable = 'LISTING_EDIT::SET_DATE_AVAILABLE',
  SetExpirationDate = 'LISTING_EDIT::SET_EXPIRATION_DATE',
  SetFilePositionNo = 'LISTING_EDIT::SET_FILE_POSITION_NO',
  SetGrossIncome = 'LISTING_EDIT::SET_GROSS_INCOME',
  SetHasOwnerThankYouBeenSent = 'LISTING_EDIT::SET_HAS_OWNER_THANK_YOU_BEEN_SENT',
  SetHasPhysicalFileBeenCreated = 'LISTING_EDIT::SET_HAS_PHYSICAL_FILE_BEEN_CREATED',
  SetHasSentCcimMailbridge = 'LISTING_EDIT::SET_HAS_SENT_CCIM_MAILBRIDGE',
  SetHasSentConstantContact = 'LISTING_EDIT::SET_HAS_SENT_CONSTANT_CONTACT',
  SetHasSentDealmakers = 'LISTING_EDIT::SET_HAS_SENT_DEALMAKERS',
  SetHasSentLocalMlsBlast = 'LISTING_EDIT::SET_HAS_SENT_LOCAL_MLS_BLAST',
  SetHasSentMarketingToCoC = 'LISTING_EDIT::SET_HAS_SENT_MARKETING_TO_COC',
  SetHasSentMarketingToEDC = 'LISTING_EDIT::SET_HAS_SENT_MARKETING_TO_EDC',
  SetHasSentOwnerListingLink = 'LISTING_EDIT::SET_HAS_SENT_OWNER_LISTING_LINK',
  SetHasSentPersonalEmailToProspects = 'LISTING_EDIT::SET_HAS_SENT_PERSONAL_EMAIL_TO_PROSPECTS',
  SetHasSentSignageRequest = 'LISTING_EDIT::SET_HAS_SENT_SIGNAGE_REQUEST',
  SetHasServerFileBeenCreated = 'LISTING_EDIT::SET_HAS_SERVER_FILE_BEEN_CREATED',
  SetHasStdbBusinessList = 'LISTING_EDIT::SET_HAS_STDB_BUSINESS_LIST',
  SetHighlights = 'LISTING_EDIT::SET_HIGHLIGHTS',
  SetIsBuildToSuit = 'LISTING_EDIT::SET_IS_BUILD_TO_SUIT',
  SetIsFeatured = 'LISTING_EDIT::SET_IS_FEATURED',
  SetIsInvestmentProperty = 'LISTING_EDIT::SET_IS_INVESTMENT_PROPERTY',
  SetIsSublease = 'LISTING_EDIT::SET_IS_SUBLEASE',
  SetLeaseTerm = 'LISTING_EDIT::SET_LEASE_TERM',
  SetLeaseType = 'LISTING_EDIT::SET_LEASE_TYPE',
  SetListingAgents = 'LISTING_EDIT::SET_LISTING_AGENTS',
  SetListingFee = 'LISTING_EDIT::SET_LISTING_FEE',
  SetLot = 'LISTING_EDIT::SET_LOT',
  SetMainImage = 'LISTING_EDIT::SET_MAIN_IMAGE',
  SetNetIncome = 'LISTING_EDIT::SET_NET_INCOME',
  SetOnlineListingService = 'LISTING_EDIT::SET_ONLINE_LISTING_SERVICE',
  SetPropertyDescription = 'LISTING_EDIT::SET_PROPERTY_DESCRIPTION',
  SetRentDetails = 'LISTING_EDIT::SET_RENT_DETAILS',
  SetRentalRate = 'LISTING_EDIT::SET_RENTAL_RATE',
  SetSellingFee = 'LISTING_EDIT::SET_SELLING_FEE',
  SetAttachedTitleCommitmentPropertyDisclosureAndSurvey = 'LISTING_EDIT::SET_ATTACHED_TITLE_COMMITMENT_PROPERTY_DISCLOSURE_AND_SURVEY',
  SetAttachedOwnerRevisions = 'LISTING_EDIT::SET_ATTACHED_OWNER_REVISIONS',
  SetAttachedListingAgreement = 'LISTING_EDIT::SET_ATTACHED_LISTING_AGREEMENT',
  SetSentOwnerThankyou = 'LISTING_EDIT::SET_SENT_OWNER_THANKYOU',
  SetGeneratedDocuments = 'LISTING_EDIT::SET_SENT_GENERATED_DOCUMENTS',
  SetDeliveredSignage = 'LISTING_EDIT::SET_DELIVERED_SIGNAGE',
  SetSentCriticalDatesBlast = 'LISTING_EDIT::SET_SENT_CRITICAL_DATES_BLAST',
  SetSentMarketingPackageOwner = 'LISTING_EDIT::SET_SENT_MARKETING_PACKAGE_OWNER',
  SetSentOwnersListingPlatformLinks = 'LISTING_EDIT::SET_SENT_OWNERS_LISTING_PLATFORM_LINKS',
  SetSentMarketingPackageEDC = 'LISTING_EDIT::SET_SENT_MARKETING_PACKAGE_EDC',
  SetSentAndUploadedSignedListingAgreement = 'LISTING_EDIT::SET_SENT_AND_UPLOADED_SIGNED_LISTING_AGREEMENT',
  SetUploadedPackageToPlatforms = 'LISTING_EDIT::SET_UPLOADED_PACKAGE_TO_PLATFORMS',
  SetSentMonthlyFollowUps = 'LISTING_EDIT::SET_SENT_MONTHLY_FOLLOW_UPS',
  SetSentPersonalEmailToProspects = 'LISTING_EDIT::SET_SENT_PERSONAL_EMAIL_TO_PROSPECTS',
  SetSentPersonalEmailToProspectsOngoing = 'LISTING_EDIT::SET_SENT_PERSONAL_EMAIL_TO_PROSPECTS_ONGOING',
  SetSentMonthlyBlastListingPlatform = 'LISTING_EDIT::SET_SENT_MONTHLY_BLAST_LISTING_PLATFORMS',
  SetShowOnWebsite = 'LISTING_EDIT::SET_SHOW_ON_WEBSITE',
  SetSignageNotes = 'LISTING_EDIT::SET_SIGNAGE_NOTES',
  SetSpace = 'LISTING_EDIT::SET_SPACE',
  SetStatus = 'LISTING_EDIT::SET_STATUS',
  SetTitle = 'LISTING_EDIT::SET_TITLE',
  SetTopProspects = 'LISTING_EDIT::SET_TOP_PROSPECTS',
  SetType = 'LISTING_EDIT::SET_TYPE',
  SetVideoLinks = 'LISTING_EDIT::SET_VIDEO_LINKS',
}

interface IAddLinkedImageAction {
  image: Attachment
  type: ActionTypes.AddLinkedImage
}

interface IAddOnlineListingServiceAction {
  type: ActionTypes.AddOnlineListingService
}

interface IDeleteLinkedImageAction {
  imageId: string
  type: ActionTypes.DeleteLinkedImage
}

interface IDeleteOnlineListingServiceAction {
  index: number
  type: ActionTypes.DeleteOnlineListingService
}

interface ILoadAddListingAction {
  lotId?: string
  propertyId: string
  spaceId?: string
  type: ActionTypes.LoadAddListing
}

interface ILoadEditListingAction {
  listingId: string
  type: ActionTypes.LoadEditListing
}

interface ILoadListingFailureAction {
  error: string
  type: ActionTypes.LoadListingFailure
}

interface ILoadListingRequestAction {
  type: ActionTypes.LoadListingRequest
}

interface ILoadListingSuccessAction {
  listing: Listing
  type: ActionTypes.LoadListingSuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveListingAction {
  listing: Listing
  type: ActionTypes.SaveListing
}

interface ISaveListingFailureAction {
  error: string
  type: ActionTypes.SaveListingFailure
}

interface ISaveListingRequestAction {
  type: ActionTypes.SaveListingRequest
}

interface ISaveListingSuccessAction {
  type: ActionTypes.SaveListingSuccess
}

interface ISetActiveDateAction {
  activeDate?: number
  type: ActionTypes.SetActiveDate
}

interface ISetAdjoiningPropertyOwnersAction {
  adjoiningPropertyOwners: List<AssociatedContact>
  type: ActionTypes.SetAdjoiningPropertyOwners
}

interface ISetAskingPriceAction {
  price: Price
  type: ActionTypes.SetAskingPrice
}

interface ISetCapRateAction {
  capRate: number
  type: ActionTypes.SetCapRate
}

interface ISetDateAvailableAction {
  dateAvailable?: number
  type: ActionTypes.SetDateAvailable
}

interface ISetExpirationDateAction {
  expirationDate?: number
  type: ActionTypes.SetExpirationDate
}

interface ISetFilePositionNoAction {
  filePositionNo: string
  type: ActionTypes.SetFilePositionNo
}

interface ISetGrossIncomeAction {
  value: number
  type: ActionTypes.SetGrossIncome
}

interface ISetAttachedListingAgreement {
  value: ListingProgressV2Status
  type: ActionTypes.SetAttachedListingAgreement
}

interface ISetAttachedOwnerRevisions {
  value: ListingProgressV2Status
  type: ActionTypes.SetAttachedOwnerRevisions
}

interface ISetAttachedTitleCommitmentPropertyDisclosureAndSurvey {
  value: ListingProgressV2Status
  type: ActionTypes.SetAttachedTitleCommitmentPropertyDisclosureAndSurvey
}
interface ISetSentOwnerThankyou {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentOwnerThankyou
}

interface ISetGeneratedDocuments {
  value: ListingProgressV2Status
  type: ActionTypes.SetGeneratedDocuments
}

interface ISetDeliveredSignage {
  value: ListingProgressV2Status
  type: ActionTypes.SetDeliveredSignage
}

interface ISetSentCriticalDatesBlast {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentCriticalDatesBlast
}

interface ISetSentAndUploadedSignedListingAgreement {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentAndUploadedSignedListingAgreement
}

interface ISetSentMarketingPackageEDC {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentMarketingPackageEDC
}

interface ISetSentMarketingPackageOwner {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentMarketingPackageOwner
}

interface ISetSentOwnersListingPlatformLinks {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentOwnersListingPlatformLinks
}

interface ISetUploadedPackageToPlatforms {
  value: ListingProgressV2Status
  type: ActionTypes.SetUploadedPackageToPlatforms
}

interface ISetSentMonthlyFollowUps {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentMonthlyFollowUps
}

interface ISetSentPersonalEmailToProspects {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentPersonalEmailToProspects
}

interface ISetSentPersonalEmailToProspectsOngoing {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentPersonalEmailToProspectsOngoing
}

interface ISetSentMonthlyBlastListingPlatform {
  value: ListingProgressV2Status
  type: ActionTypes.SetSentMonthlyBlastListingPlatform
}

interface ISetHighlightsAction {
  highlights: List<string>
  type: ActionTypes.SetHighlights
}

interface ISetIsBuildToSuitAction {
  isBuildToSuit: boolean
  type: ActionTypes.SetIsBuildToSuit
}

interface ISetIsFeaturedAction {
  isFeatured: boolean
  type: ActionTypes.SetIsFeatured
}

interface ISetIsInvestmentPropertyAction {
  isInvestmentProperty: boolean
  type: ActionTypes.SetIsInvestmentProperty
}

interface ISetIsSubleaseAction {
  isSublease: boolean
  type: ActionTypes.SetIsSublease
}

interface ISetMainImageAction {
  imageId: string
  type: ActionTypes.SetMainImage
}

interface ISetLeaseTermAction {
  term: number
  type: ActionTypes.SetLeaseTerm
}

interface ISetLeaseTypeAction {
  leaseType: string
  type: ActionTypes.SetLeaseType
}

interface ISetListingAgentsAction {
  listingAgents: List<AssociatedContact>
  type: ActionTypes.SetListingAgents
}

interface ISetListingFeeAction {
  fee: number
  listingType: ListingType
  type: ActionTypes.SetListingFee
}

interface ISetLotAction {
  lotId: string
  type: ActionTypes.SetLot
}

interface ISetNetIncomeAction {
  value: number
  type: ActionTypes.SetNetIncome
}

interface ISetOnlineListingServiceAction {
  index: number
  listingService: OnlineListingService
  type: ActionTypes.SetOnlineListingService
}

interface ISetPropertyDescriptionAction {
  text: string
  type: ActionTypes.SetPropertyDescription
}

interface ISetRentalRateAction {
  rate: RentalRate
  type: ActionTypes.SetRentalRate
}

interface ISetRentDetailsAction {
  value: List<ITenantRent>
  type: ActionTypes.SetRentDetails
}

interface ISetSellingFeeAction {
  fee: number
  listingType: ListingType
  type: ActionTypes.SetSellingFee
}

interface ISetShowOnWebsiteAction {
  showOnWebsite: boolean
  type: ActionTypes.SetShowOnWebsite
}

interface ISetSignageNotesAction {
  notes: string
  type: ActionTypes.SetSignageNotes
}

interface ISetSpaceAction {
  spaceId: string
  type: ActionTypes.SetSpace
}

interface ISetStatusAction {
  status: string
  type: ActionTypes.SetStatus
}

interface ISetTitleAction {
  title: string
  type: ActionTypes.SetTitle
}

interface ISetTopProspectsAction {
  topProspects: List<AssociatedContact>
  type: ActionTypes.SetTopProspects
}

interface ISetTypeAction {
  listingType: string
  type: ActionTypes.SetType
}

interface ISetVideoLinks {
  type: ActionTypes.SetVideoLinks
  videoLinks: List<string>
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IAddLinkedImageAction
  | IAddOnlineListingServiceAction
  | IDeleteLinkedImageAction
  | IDeleteOnlineListingServiceAction
  | ILoadListingFailureAction
  | ILoadListingRequestAction
  | ILoadListingSuccessAction
  | IResetStateAction
  | ISaveListingFailureAction
  | ISaveListingRequestAction
  | ISaveListingSuccessAction
  | ISetActiveDateAction
  | ISetAdjoiningPropertyOwnersAction
  | ISetAskingPriceAction
  | ISetCapRateAction
  | ISetDateAvailableAction
  | ISetExpirationDateAction
  | ISetFilePositionNoAction
  | ISetGrossIncomeAction
  | ISetAttachedListingAgreement
  | ISetAttachedOwnerRevisions
  | ISetAttachedTitleCommitmentPropertyDisclosureAndSurvey
  | ISetGeneratedDocuments
  | ISetDeliveredSignage
  | ISetSentCriticalDatesBlast
  | ISetSentAndUploadedSignedListingAgreement
  | ISetSentMarketingPackageEDC
  | ISetSentMarketingPackageOwner
  | ISetSentMonthlyBlastListingPlatform
  | ISetSentMonthlyFollowUps
  | ISetSentOwnerThankyou
  | ISetSentOwnersListingPlatformLinks
  | ISetSentPersonalEmailToProspects
  | ISetSentPersonalEmailToProspectsOngoing
  | ISetUploadedPackageToPlatforms
  | ISetHighlightsAction
  | ISetIsBuildToSuitAction
  | ISetIsFeaturedAction
  | ISetIsInvestmentPropertyAction
  | ISetIsSubleaseAction
  | ISetLeaseTermAction
  | ISetLeaseTypeAction
  | ISetListingAgentsAction
  | ISetListingFeeAction
  | ISetLotAction
  | ISetMainImageAction
  | ISetNetIncomeAction
  | ISetOnlineListingServiceAction
  | ISetPropertyDescriptionAction
  | ISetRentalRateAction
  | ISetRentDetailsAction
  | ISetSellingFeeAction
  | ISetShowOnWebsiteAction
  | ISetSignageNotesAction
  | ISetSpaceAction
  | ISetStatusAction
  | ISetTitleAction
  | ISetTopProspectsAction
  | ISetTypeAction
  | ISetVideoLinks

// -------------------------
// ACTION CREATORS
// -------------------------
function addLinkedImage(image: Attachment): IAddLinkedImageAction {
  return {
    image,
    type: ActionTypes.AddLinkedImage,
  }
}

function addOnlineListingService(): IAddOnlineListingServiceAction {
  return { type: ActionTypes.AddOnlineListingService }
}

function deleteLinkedImage(imageId: string): IDeleteLinkedImageAction {
  return {
    imageId,
    type: ActionTypes.DeleteLinkedImage,
  }
}

function deleteOnlineListingService(
  index: number,
): IDeleteOnlineListingServiceAction {
  return {
    index,
    type: ActionTypes.DeleteOnlineListingService,
  }
}

function loadAddListing(
  propertyId: string,
  spaceId?: string,
  lotId?: string,
): ILoadAddListingAction {
  return {
    lotId,
    propertyId,
    spaceId,
    type: ActionTypes.LoadAddListing,
  }
}

function loadEditListing(listingId: string): ILoadEditListingAction {
  return {
    listingId,
    type: ActionTypes.LoadEditListing,
  }
}

function loadListingFailure(error: string): ILoadListingFailureAction {
  return {
    error,
    type: ActionTypes.LoadListingFailure,
  }
}

function loadListingRequest(): ILoadListingRequestAction {
  return { type: ActionTypes.LoadListingRequest }
}

function loadListingSuccess(listing: Listing): ILoadListingSuccessAction {
  return {
    listing,
    type: ActionTypes.LoadListingSuccess,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function saveListing(listing: Listing): ISaveListingAction {
  return {
    listing,
    type: ActionTypes.SaveListing,
  }
}

function saveListingFailure(error: string): ISaveListingFailureAction {
  return {
    error,
    type: ActionTypes.SaveListingFailure,
  }
}

function saveListingRequest(): ISaveListingRequestAction {
  return { type: ActionTypes.SaveListingRequest }
}

function saveListingSuccess(): ISaveListingSuccessAction {
  return {
    type: ActionTypes.SaveListingSuccess,
  }
}

function setActiveDate(activeDate?: number): ISetActiveDateAction {
  return {
    activeDate,
    type: ActionTypes.SetActiveDate,
  }
}

function setAdjoiningPropertyOwners(
  adjoiningPropertyOwners: List<AssociatedContact>,
): ISetAdjoiningPropertyOwnersAction {
  return {
    adjoiningPropertyOwners,
    type: ActionTypes.SetAdjoiningPropertyOwners,
  }
}

function setAskingPrice(price: Price): ISetAskingPriceAction {
  return {
    price,
    type: ActionTypes.SetAskingPrice,
  }
}

function setCapRate(capRate: number): ISetCapRateAction {
  return {
    capRate,
    type: ActionTypes.SetCapRate,
  }
}

function setDateAvailable(dateAvailable?: number): ISetDateAvailableAction {
  return {
    dateAvailable,
    type: ActionTypes.SetDateAvailable,
  }
}

function setExpirationDate(expirationDate?: number): ISetExpirationDateAction {
  return {
    expirationDate,
    type: ActionTypes.SetExpirationDate,
  }
}

function setFilePositionNo(filePositionNo: string): ISetFilePositionNoAction {
  return {
    filePositionNo,
    type: ActionTypes.SetFilePositionNo,
  }
}

function setGrossIncome(value: number): ISetGrossIncomeAction {
  return {
    type: ActionTypes.SetGrossIncome,
    value,
  }
}

function setSentOwnerThankyou(
  value: ListingProgressV2Status,
): ISetSentOwnerThankyou {
  return {
    type: ActionTypes.SetSentOwnerThankyou,
    value,
  }
}

function setAttachedListingAgreement(
  value: ListingProgressV2Status,
): ISetAttachedListingAgreement {
  return {
    type: ActionTypes.SetAttachedListingAgreement,
    value,
  }
}

function setAttachedOwnerRevisions(
  value: ListingProgressV2Status,
): ISetAttachedOwnerRevisions {
  return {
    type: ActionTypes.SetAttachedOwnerRevisions,
    value,
  }
}

function setAttachedTitleCommitmentPropertyDisclosureAndSurvey(
  value: ListingProgressV2Status,
): ISetAttachedTitleCommitmentPropertyDisclosureAndSurvey {
  return {
    type: ActionTypes.SetAttachedTitleCommitmentPropertyDisclosureAndSurvey,
    value,
  }
}

function setSentCriticalDatesBlast(
  value: ListingProgressV2Status,
): ISetSentCriticalDatesBlast {
  return {
    type: ActionTypes.SetSentCriticalDatesBlast,
    value,
  }
}

function setSentAndUploadedSignedListingAgreement(
  value: ListingProgressV2Status,
): ISetSentAndUploadedSignedListingAgreement {
  return {
    type: ActionTypes.SetSentAndUploadedSignedListingAgreement,
    value,
  }
}

function setSentMarketingPackageEDC(
  value: ListingProgressV2Status,
): ISetSentMarketingPackageEDC {
  return {
    type: ActionTypes.SetSentMarketingPackageEDC,
    value,
  }
}

function setSentMarketingPackageOwner(
  value: ListingProgressV2Status,
): ISetSentMarketingPackageOwner {
  return {
    type: ActionTypes.SetSentMarketingPackageOwner,
    value,
  }
}

function setSentMonthlyBlastListingPlatform(
  value: ListingProgressV2Status,
): ISetSentMonthlyBlastListingPlatform {
  return {
    type: ActionTypes.SetSentMonthlyBlastListingPlatform,
    value,
  }
}

function setSentMonthlyFollowUps(
  value: ListingProgressV2Status,
): ISetSentMonthlyFollowUps {
  return {
    value,
    type: ActionTypes.SetSentMonthlyFollowUps,
  }
}

function setSentOwnersListingPlatformLinks(
  value: ListingProgressV2Status,
): ISetSentOwnersListingPlatformLinks {
  return {
    value,
    type: ActionTypes.SetSentOwnersListingPlatformLinks,
  }
}

function setSentPersonalEmailToProspects(
  value: ListingProgressV2Status,
): ISetSentPersonalEmailToProspects {
  return {
    value,
    type: ActionTypes.SetSentPersonalEmailToProspects,
  }
}

function setSentPersonalEmailToProspectsOngoing(
  value: ListingProgressV2Status,
): ISetSentPersonalEmailToProspectsOngoing {
  return {
    value,
    type: ActionTypes.SetSentPersonalEmailToProspectsOngoing,
  }
}

function setGeneratedDocuments(
  value: ListingProgressV2Status,
): ISetGeneratedDocuments {
  return {
    type: ActionTypes.SetGeneratedDocuments,
    value,
  }
}

function setDeliveredSignage(
  value: ListingProgressV2Status,
): ISetDeliveredSignage {
  return {
    type: ActionTypes.SetDeliveredSignage,
    value,
  }
}

function setUploadedPackageToPlatforms(
  value: ListingProgressV2Status,
): ISetUploadedPackageToPlatforms {
  return {
    type: ActionTypes.SetUploadedPackageToPlatforms,
    value,
  }
}

function setHighlights(highlights: List<string>): ISetHighlightsAction {
  return {
    highlights,
    type: ActionTypes.SetHighlights,
  }
}

function setIsBuildToSuit(isBuildToSuit: boolean): ISetIsBuildToSuitAction {
  return {
    isBuildToSuit,
    type: ActionTypes.SetIsBuildToSuit,
  }
}

function setIsFeatured(isFeatured: boolean): ISetIsFeaturedAction {
  return {
    isFeatured,
    type: ActionTypes.SetIsFeatured,
  }
}

function setIsInvestmentProperty(
  isInvestmentProperty: boolean,
): ISetIsInvestmentPropertyAction {
  return {
    isInvestmentProperty,
    type: ActionTypes.SetIsInvestmentProperty,
  }
}

function setIsSublease(isSublease: boolean): ISetIsSubleaseAction {
  return {
    isSublease,
    type: ActionTypes.SetIsSublease,
  }
}

function setLeaseTerm(term: number): ISetLeaseTermAction {
  return {
    term,
    type: ActionTypes.SetLeaseTerm,
  }
}

function setLeaseType(leaseType: string): ISetLeaseTypeAction {
  return {
    leaseType,
    type: ActionTypes.SetLeaseType,
  }
}

function setListingAgents(
  listingAgents: List<AssociatedContact>,
): ISetListingAgentsAction {
  return {
    listingAgents,
    type: ActionTypes.SetListingAgents,
  }
}

function setListingFee(
  fee: number,
  listingType: ListingType,
): ISetListingFeeAction {
  return {
    fee,
    listingType,
    type: ActionTypes.SetListingFee,
  }
}

function setLot(lotId: string): ISetLotAction {
  return {
    lotId,
    type: ActionTypes.SetLot,
  }
}

function setMainImage(imageId: string): ISetMainImageAction {
  return {
    imageId,
    type: ActionTypes.SetMainImage,
  }
}

function setNetIncome(value: number): ISetNetIncomeAction {
  return {
    type: ActionTypes.SetNetIncome,
    value,
  }
}

function setOnlineListingService(
  index: number,
  listingService: OnlineListingService,
): ISetOnlineListingServiceAction {
  return {
    index,
    listingService,
    type: ActionTypes.SetOnlineListingService,
  }
}

function setPropertyDescription(text: string): ISetPropertyDescriptionAction {
  return {
    text,
    type: ActionTypes.SetPropertyDescription,
  }
}

function setRentalRate(rate: RentalRate): ISetRentalRateAction {
  return {
    rate,
    type: ActionTypes.SetRentalRate,
  }
}

function setRentDetails(value: List<ITenantRent>): ISetRentDetailsAction {
  return {
    type: ActionTypes.SetRentDetails,
    value,
  }
}

function setSellingFee(
  fee: number,
  listingType: ListingType,
): ISetSellingFeeAction {
  return {
    fee,
    listingType,
    type: ActionTypes.SetSellingFee,
  }
}

function setShowOnWebsite(showOnWebsite: boolean): ISetShowOnWebsiteAction {
  return {
    showOnWebsite,
    type: ActionTypes.SetShowOnWebsite,
  }
}

function setSignageNotes(notes: string): ISetSignageNotesAction {
  return {
    notes,
    type: ActionTypes.SetSignageNotes,
  }
}

function setSpace(spaceId: string): ISetSpaceAction {
  return {
    spaceId,
    type: ActionTypes.SetSpace,
  }
}

function setStatus(status: string): ISetStatusAction {
  return {
    status,
    type: ActionTypes.SetStatus,
  }
}

function setTopProspects(
  topProspects: List<AssociatedContact>,
): ISetTopProspectsAction {
  return {
    topProspects,
    type: ActionTypes.SetTopProspects,
  }
}

function setTitle(title: string): ISetTitleAction {
  return {
    title,
    type: ActionTypes.SetTitle,
  }
}

function setType(listingType: string): ISetTypeAction {
  return {
    listingType,
    type: ActionTypes.SetType,
  }
}

function setVideoLinks(videoLinks: List<string>): ISetVideoLinks {
  return {
    type: ActionTypes.SetVideoLinks,
    videoLinks,
  }
}

export const actionCreators = {
  addLinkedImage,
  addOnlineListingService,
  deleteLinkedImage,
  deleteOnlineListingService,
  loadAddListing,
  loadEditListing,
  resetState,
  saveListing,
  setActiveDate,
  setAdjoiningPropertyOwners,
  setAskingPrice,
  setCapRate,
  setDateAvailable,
  setExpirationDate,
  setFilePositionNo,
  setGrossIncome,
  setAttachedListingAgreement,
  setAttachedOwnerRevisions,
  setAttachedTitleCommitmentPropertyDisclosureAndSurvey,
  setDeliveredSignage,
  setGeneratedDocuments,
  setSentCriticalDatesBlast,
  setSentAndUploadedSignedListingAgreement,
  setSentMarketingPackageEDC,
  setSentMarketingPackageOwner,
  setSentMonthlyBlastListingPlatform,
  setSentMonthlyFollowUps,
  setSentOwnerThankyou,
  setSentOwnersListingPlatformLinks,
  setSentPersonalEmailToProspects,
  setSentPersonalEmailToProspectsOngoing,
  setUploadedPackageToPlatforms,
  setHighlights,
  setIsBuildToSuit,
  setIsFeatured,
  setIsInvestmentProperty,
  setIsSublease,
  setLeaseTerm,
  setLeaseType,
  setListingAgents,
  setListingFee,
  setLot,
  setMainImage,
  setNetIncome,
  setOnlineListingService,
  setPropertyDescription,
  setRentDetails,
  setRentalRate,
  setSellingFee,
  setShowOnWebsite,
  setSignageNotes,
  setSpace,
  setStatus,
  setTitle,
  setTopProspects,
  setType,
  setVideoLinks,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ListingEditState> = (
  state: ListingEditState = new ListingEditState(),
  incomingAction: Action,
): ListingEditState => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.AddLinkedImage:
      if (
        state.listing.photos.findIndex(
          (p) => p !== undefined && p.id === action.image.id,
        ) === -1
      ) {
        const newPhotos = state.listing.photos.push(action.image)
        const thumbnailImageId = !state.listing.thumbnailImageId
          ? action.image.id
          : state.listing.thumbnailImageId

        return state
          .setIn(['listing', 'photos'], newPhotos)
          .setIn(
            ['listing', 'thumbnailImageId'],
            thumbnailImageId,
          ) as ListingEditState
      } else {
        return state
      }

    case ActionTypes.AddOnlineListingService:
      return state.setIn(
        ['listing', 'listingServices'],
        state.listing.listingServices.push(new OnlineListingService()),
      ) as ListingEditState

    case ActionTypes.DeleteLinkedImage: {
      const idx = state.listing.photos.findIndex(
        (p) => p !== undefined && p.id === action.imageId,
      )
      if (idx > -1) {
        const newPhotos = state.listing.photos.delete(idx)
        let thumbnailImageId = state.listing.thumbnailImageId

        if (thumbnailImageId === action.imageId) {
          thumbnailImageId = newPhotos.size > 0 ? newPhotos.first().id : ''
        }

        return state
          .setIn(['listing', 'photos'], newPhotos)
          .setIn(
            ['listing', 'thumbnailImageId'],
            thumbnailImageId,
          ) as ListingEditState
      } else {
        return state
      }
    }

    case ActionTypes.DeleteOnlineListingService:
      if (
        state.hasIn(['listing', 'listingServices', action.index]) &&
        !state.getIn(['listing', 'listingServices', action.index, 'required'])
      ) {
        return state.deleteIn([
          'listing',
          'listingServices',
          action.index,
        ]) as ListingEditState
      } else {
        return state
      }

    case ActionTypes.LoadListingFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false)
        .set('isReady', false) as ListingEditState

    case ActionTypes.LoadListingRequest:
      return state
        .set('isLoading', true)
        .set('isReady', false) as ListingEditState

    case ActionTypes.LoadListingSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('isReady', true)
        .set('listing', action.listing) as ListingEditState

    case ActionTypes.ResetState:
      return state
        .set('error', undefined)
        .set('isReady', false)
        .set('listing', new Listing()) as ListingEditState

    case ActionTypes.SaveListingFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as ListingEditState

    case ActionTypes.SaveListingRequest:
      return state.set('isSaving', true) as ListingEditState

    case ActionTypes.SaveListingSuccess:
      return state
        .set('error', undefined)
        .set('isSaving', false) as ListingEditState

    case ActionTypes.SetActiveDate:
      return state.setIn(
        ['listing', 'activeDate'],
        action.activeDate,
      ) as ListingEditState

    case ActionTypes.SetAdjoiningPropertyOwners:
      return state.setIn(
        ['listing', 'adjoiningPropertyOwners'],
        action.adjoiningPropertyOwners,
      ) as ListingEditState

    case ActionTypes.SetAskingPrice:
      return state.setIn(
        ['listing', 'saleData', 'askingPrice'],
        action.price,
      ) as ListingEditState

    case ActionTypes.SetCapRate:
      return state.setIn(
        ['listing', 'saleData', 'capRate'],
        action.capRate,
      ) as ListingEditState

    case ActionTypes.SetDateAvailable:
      return state.setIn(
        ['listing', 'leaseData', 'dateAvailable'],
        action.dateAvailable,
      ) as ListingEditState

    case ActionTypes.SetExpirationDate:
      return state.setIn(
        ['listing', 'expirationDate'],
        action.expirationDate,
      ) as ListingEditState

    case ActionTypes.SetFilePositionNo:
      return state.setIn(
        ['listing', 'filePositionNo'],
        action.filePositionNo,
      ) as ListingEditState

    case ActionTypes.SetGrossIncome:
      return state.setIn(
        ['listing', 'financialData', 'grossIncome'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentOwnerThankyou:
      console.log(state)
      console.log(action)
      console.log(state.listing.progressV2.twoWeekDeadline.sentOwnerThankyou)
      return state.setIn(
        ['listing', 'progressV2', 'twoWeekDeadline', 'sentOwnerThankyou'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetGeneratedDocuments:
      return state.setIn(
        ['listing', 'progressV2', 'twoWeekDeadline', 'generatedDocuments'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetAttachedListingAgreement:
      return state.setIn(
        [
          'listing',
          'progressV2',
          'twoWeekDeadline',
          'attachedListingAgreement',
        ],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetAttachedTitleCommitmentPropertyDisclosureAndSurvey:
      return state.setIn(
        [
          'listing',
          'progressV2',
          'twoWeekDeadline',
          'attachedTitleCommitmentPropertyDisclosureAndSurvey',
        ],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetAttachedOwnerRevisions:
      return state.setIn(
        ['listing', 'progressV2', 'twoWeekDeadline', 'attachedOwnerRevisions'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetDeliveredSignage:
      return state.setIn(
        ['listing', 'progressV2', 'twoWeekDeadline', 'deliveredSignage'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentCriticalDatesBlast:
      return state.setIn(
        ['listing', 'progressV2', 'twoWeekDeadline', 'sentCriticalDatesBlast'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentMarketingPackageOwner:
      return state.setIn(
        [
          'listing',
          'progressV2',
          'twoWeekDeadline',
          'sentMarketingPackageOwner',
        ],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentOwnersListingPlatformLinks:
      return state.setIn(
        [
          'listing',
          'progressV2',
          'twoWeekDeadline',
          'sentOwnersListingPlatformLinks',
        ],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentMarketingPackageEDC:
      return state.setIn(
        ['listing', 'progressV2', 'twoWeekDeadline', 'sentMarketingPackageEDC'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentAndUploadedSignedListingAgreement:
      return state.setIn(
        [
          'listing',
          'progressV2',
          'twoWeekDeadline',
          'sentAndUploadedSignedListingAgreement',
        ],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetUploadedPackageToPlatforms:
      return state.setIn(
        [
          'listing',
          'progressV2',
          'twoWeekDeadline',
          'uploadedPackageToPlatforms',
        ],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentMonthlyFollowUps:
      return state.setIn(
        ['listing', 'progressV2', 'ongoing', 'sentMonthlyFollowups'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentPersonalEmailToProspects:
      return state.setIn(
        [
          'listing',
          'progressV2',
          'twoWeekDeadline',
          'sentPersonalEmailToProspects',
        ],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentPersonalEmailToProspectsOngoing:
      return state.setIn(
        ['listing', 'progressV2', 'ongoing', 'sentPersonalEmailToProspects'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSentMonthlyBlastListingPlatform:
      return state.setIn(
        ['listing', 'progressV2', 'ongoing', 'sentMonthlyBlastListingPlatform'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetHighlights:
      return state.setIn(
        ['listing', 'highlights'],
        action.highlights,
      ) as ListingEditState

    case ActionTypes.SetIsBuildToSuit:
      return state.setIn(
        ['listing', 'leaseData', 'buildToSuit'],
        action.isBuildToSuit,
      ) as ListingEditState

    case ActionTypes.SetIsFeatured:
      const newState = state.setIn(['listing', 'showOnWebsite'], true)
      return newState.setIn(
        ['listing', 'isFeatured'],
        action.isFeatured,
      ) as ListingEditState

    case ActionTypes.SetIsInvestmentProperty:
      return state.setIn(
        ['listing', 'isInvestmentProperty'],
        action.isInvestmentProperty,
      ) as ListingEditState

    case ActionTypes.SetIsSublease:
      return state.setIn(
        ['listing', 'leaseData', 'sublease'],
        action.isSublease,
      ) as ListingEditState

    case ActionTypes.SetLeaseTerm:
      return state.setIn(
        ['listing', 'leaseData', 'leaseTermInMonths'],
        action.term,
      ) as ListingEditState

    case ActionTypes.SetLeaseType:
      return state.setIn(
        ['listing', 'leaseData', 'leaseType'],
        action.leaseType,
      ) as ListingEditState

    case ActionTypes.SetListingAgents:
      return state.setIn(
        ['listing', 'listingAgents'],
        action.listingAgents,
      ) as ListingEditState

    case ActionTypes.SetListingFee:
      if (action.listingType === ListingType.Lease) {
        return state.setIn(
          ['listing', 'leaseData', 'commissionSplit', 'listingFee'],
          action.fee,
        ) as ListingEditState
      } else {
        return state.setIn(
          ['listing', 'saleData', 'commissionSplit', 'listingFee'],
          action.fee,
        ) as ListingEditState
      }

    case ActionTypes.SetLot: {
      const lot = action.lotId
        ? state.listing.property.lotDetails.find(
            (l) => l !== undefined && l.id === action.lotId,
          )
        : undefined

      return state
        .setIn(['listing', 'lot'], lot)
        .setIn(['listing', 'lotId'], action.lotId) as ListingEditState
    }

    case ActionTypes.SetMainImage:
      if (
        state.listing.photos.findIndex(
          (p) => p !== undefined && p.id === action.imageId,
        ) > -1
      ) {
        return state.setIn(
          ['listing', 'thumbnailImageId'],
          action.imageId,
        ) as ListingEditState
      } else {
        return state
      }

    case ActionTypes.SetNetIncome:
      return state.setIn(
        ['listing', 'financialData', 'netIncome'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetOnlineListingService:
      if (state.hasIn(['listing', 'listingServices', action.index])) {
        return state.setIn(
          ['listing', 'listingServices', action.index],
          action.listingService,
        ) as ListingEditState
      } else {
        return state
      }

    case ActionTypes.SetPropertyDescription:
      return state.setIn(
        ['listing', 'propertyDescription'],
        action.text,
      ) as ListingEditState

    case ActionTypes.SetRentalRate:
      return state.setIn(
        ['listing', 'leaseData', 'rentalRate'],
        action.rate,
      ) as ListingEditState

    case ActionTypes.SetRentDetails:
      return state.setIn(
        ['listing', 'financialData', 'rentDetails'],
        action.value,
      ) as ListingEditState

    case ActionTypes.SetSellingFee:
      if (action.listingType === ListingType.Lease) {
        return state.setIn(
          ['listing', 'leaseData', 'commissionSplit', 'sellingFee'],
          action.fee,
        ) as ListingEditState
      } else {
        return state.setIn(
          ['listing', 'saleData', 'commissionSplit', 'sellingFee'],
          action.fee,
        ) as ListingEditState
      }

    case ActionTypes.SetShowOnWebsite:
      return state
        .setIn(
          ['listing', 'isFeatured'],
          action.showOnWebsite === false ? false : state.listing.isFeatured,
        )
        .setIn(
          ['listing', 'showOnWebsite'],
          action.showOnWebsite,
        ) as ListingEditState

    case ActionTypes.SetSignageNotes:
      return state.setIn(
        ['listing', 'signageNotes'],
        action.notes,
      ) as ListingEditState

    case ActionTypes.SetSpace: {
      const space = action.spaceId
        ? state.listing.property.spaceDetails.find(
            (s) => s !== undefined && s.id === action.spaceId,
          )
        : undefined

      return state
        .setIn(['listing', 'space'], space)
        .setIn(['listing', 'spaceId'], action.spaceId) as ListingEditState
    }

    case ActionTypes.SetStatus:
      return state.setIn(
        ['listing', 'status'],
        action.status,
      ) as ListingEditState

    case ActionTypes.SetTitle:
      return state.setIn(['listing', 'title'], action.title) as ListingEditState

    case ActionTypes.SetTopProspects:
      return state.setIn(
        ['listing', 'topProspects'],
        action.topProspects,
      ) as ListingEditState

    case ActionTypes.SetType: {
      const saleData =
        action.listingType !== ListingType.Lease && !state.listing.saleData
          ? new ListingSaleData()
          : state.listing.saleData
      const leaseData =
        action.listingType === ListingType.Lease && !state.listing.leaseData
          ? new ListingLeaseData()
          : state.listing.leaseData

      return state
        .setIn(['listing', 'leaseData'], leaseData)
        .setIn(['listing', 'saleData'], saleData)
        .setIn(['listing', 'type'], action.listingType) as ListingEditState
    }

    case ActionTypes.SetVideoLinks:
      return state.setIn(
        ['listing', 'videoLinks'],
        action.videoLinks,
      ) as ListingEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadAddListingSaga(action: ILoadAddListingAction) {
  try {
    yield put(loadListingRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/Properties/${action.propertyId}`,
    )

    if (result.success === true) {
      const property = Property.fromJS(result.entity)
      const lot = action.lotId
        ? property.lotDetails.find(
            (l) => l !== undefined && l.id === action.lotId,
          )
        : undefined
      const space = action.spaceId
        ? property.spaceDetails.find(
            (s) => s !== undefined && s.id === action.spaceId,
          )
        : undefined

      const listing = new Listing({
        highlights: property.highlights,
        listingServices: List([
          new OnlineListingService({ name: 'LoopNet', required: true }),
          new OnlineListingService({ name: 'Crexi', required: true }),
        ]),
        lot,
        lotId: action.lotId,
        property,
        propertyDescription: property.propertyDescription,
        saleData: new ListingSaleData(),
        space,
        spaceId: action.spaceId,
        type: ListingType.Sale,
      })
      yield put(loadListingSuccess(listing))
    } else {
      yield put(loadListingFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadListingFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadEditListingSaga(action: ILoadEditListingAction) {
  try {
    yield put(loadListingRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/Listings/${action.listingId}`,
    )

    if (result.success === true) {
      yield put(loadListingSuccess(Listing.fromJS(result.entity)))
    } else {
      yield put(loadListingFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadListingFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* saveListingSaga(action: ISaveListingAction) {
  try {
    yield put(saveListingRequest())

    const files: IListingEditFiles = yield select(
      createStructuredSelector<ApplicationState, IListingEditFiles>({
        flyerFiles: selectFlyerFiles(),
        marketingPackageFiles: selectMarketingPackageFiles(),
      }),
    )

    const listing = action.listing.toJS()

    listing.flyer =
      files.flyerFiles && files.flyerFiles.size > 0
        ? files.flyerFiles.first().attachment.toJS()
        : undefined

    if (listing.flyer) {
      listing.flyerId = listing.flyer.id
    }

    listing.marketingPackage =
      files.marketingPackageFiles && files.marketingPackageFiles.size > 0
        ? files.marketingPackageFiles.first().attachment.toJS()
        : undefined

    if (listing.marketingPackage) {
      listing.marketingPackageId = listing.marketingPackage.id
    }

    const reqOpts: IRequestOptions = {
      body: JSON.stringify(listing),
      method: listing.id ? RequestMethod.PUT : RequestMethod.POST,
    }

    const reqUrl = listing.id ? `/api/Listings/${listing.id}` : '/api/Listings'

    const result = yield makeAuthenticatedRequest(reqUrl, reqOpts)

    if (result.success === true) {
      yield put(saveListingSuccess())
      yield put(push(`/brokerage/listing/${listing.id || result.entity.id}`))
      yield put(successToast('Success!', `${listing.title} saved`))
    } else {
      yield put(saveListingFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveListingFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadAddListing() {
  yield takeLatest(ActionTypes.LoadAddListing, loadAddListingSaga)
}

function* watchLoadEditListing() {
  yield takeLatest(ActionTypes.LoadEditListing, loadEditListingSaga)
}

function* watchSaveListing() {
  yield takeLatest(ActionTypes.SaveListing, saveListingSaga)
}

export function* listingEditRootSaga() {
  yield fork(watchLoadAddListing)
  yield fork(watchLoadEditListing)
  yield fork(watchSaveListing)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) => state.listingEdit

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectFlyerFiles = () => selectFiles('listing-flyer')

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectIsReady = () =>
  createSelector(selectModuleState(), (mState) => mState.isReady)

export const selectIsSaving = () =>
  createSelector(selectModuleState(), (mState) => mState.isSaving)

export const selectListing = () =>
  createSelector(selectModuleState(), (mState) => mState.listing)

export const selectMarketingPackageFiles = () =>
  selectFiles('listing-marketing-pkg')
